import { supabase } from '../lib/supabase';
import type { Project } from '../components/dashboard/projects/ProjectCard';

export interface ProjectCreateData {
  title: string;
  company: string;
  role: string;
  department: string;
  location: string;
  status: 'active' | 'paused' | 'completed' | 'draft' | 'archived';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  deadline: string;
  budget: {
    min: number;
    max: number;
    currency: string;
  };
  team: Array<{
    id: string;
    name: string;
    role: string;
    avatar?: string;
  }>;
  requirements: {
    experience: string;
    skills: string[];
    education: string;
  };
}

export interface ProjectUpdateData extends Partial<ProjectCreateData> {
  id: string;
  progress?: {
    applicants: number;
    interviews: number;
    offers: number;
    hires: number;
  };
  analytics?: {
    totalViews: number;
    applicationRate: number;
    qualityScore: number;
  };
}

class ProjectsService {
  /**
   * Get all projects for the current user
   */
  async getProjects(): Promise<Project[]> {
    const { data, error } = await supabase
      .from('projects')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching projects:', error);
      throw new Error('Failed to fetch projects');
    }

    return data.map(this.mapDatabaseToProject);
  }

  /**
   * Get a specific project by ID
   */
  async getProject(id: string): Promise<Project | null> {
    const { data, error } = await supabase
      .from('projects')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        return null; // Project not found
      }
      console.error('Error fetching project:', error);
      throw new Error('Failed to fetch project');
    }

    return this.mapDatabaseToProject(data);
  }

  /**
   * Create a new project
   */
  async createProject(projectData: ProjectCreateData): Promise<Project> {
    const { data: user } = await supabase.auth.getUser();
    if (!user.user) {
      throw new Error('User not authenticated');
    }

    // Get user's organization_id from profile
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('organization_id')
      .eq('user_id', user.user.id)
      .single();

    if (profileError) {
      console.error('Error fetching user profile:', profileError);
      // Continue without organization_id if profile fetch fails
    }

    // Use the existing projects table schema (from enterprise cleanup migration)
    const databaseProject = {
      user_id: user.user.id,
      organization_id: profile?.organization_id || null,
      title: projectData.title,
      description: `${projectData.role} position in ${projectData.department}`, // Combine role and department
      company: projectData.company,
      location: projectData.location,
      department: projectData.department,
      salary_range: projectData.budget ? `${projectData.budget.min}-${projectData.budget.max} ${projectData.budget.currency}` : null,
      status: projectData.status,
      priority: projectData.priority,
      deadline: projectData.deadline,
      progress: {
        applicants: 0,
        interviews: 0,
        offers: 0,
        hires: 0
      },
      requirements: projectData.requirements?.skills || [],
      tags: ['hiring', 'project'],
    };

    const { data, error } = await supabase
      .from('projects')
      .insert(databaseProject)
      .select()
      .single();

    if (error) {
      console.error('❌ Database error details:', error);
      console.error('❌ Error code:', error.code);
      console.error('❌ Error message:', error.message);
      console.error('❌ Error details:', error.details);
      console.error('❌ Error hint:', error.hint);
      throw new Error(`Failed to create project: ${error.message}`);
    }

    return this.mapDatabaseToProject(data);
  }

  /**
   * Update an existing project
   */
  async updateProject(projectData: ProjectUpdateData): Promise<Project> {
    const updateData: any = {
      updated_at: new Date().toISOString(),
    };

    // Map frontend fields to existing database schema
    if (projectData.title) updateData.title = projectData.title;
    if (projectData.company) updateData.company = projectData.company;
    if (projectData.role && projectData.department) {
      updateData.description = `${projectData.role} position in ${projectData.department}`;
    }
    if (projectData.department) updateData.department = projectData.department;
    if (projectData.location) updateData.location = projectData.location;
    if (projectData.status) updateData.status = projectData.status;
    if (projectData.priority) updateData.priority = projectData.priority;
    if (projectData.deadline) updateData.deadline = projectData.deadline;
    
    if (projectData.budget) {
      updateData.salary_range = `${projectData.budget.min}-${projectData.budget.max} ${projectData.budget.currency}`;
    }
    
    if (projectData.requirements) {
      updateData.requirements = projectData.requirements.skills || [];
    }
    
    if (projectData.progress) {
      updateData.progress = {
        applicants: projectData.progress.applicants,
        interviews: projectData.progress.interviews,
        offers: projectData.progress.offers,
        hires: projectData.progress.hires
      };
    }

    const { data, error } = await supabase
      .from('projects')
      .update(updateData)
      .eq('id', projectData.id)
      .select()
      .single();

    if (error) {
      console.error('Error updating project:', error);
      throw new Error('Failed to update project');
    }

    return this.mapDatabaseToProject(data);
  }

  /**
   * Delete a project
   */
  async deleteProject(id: string): Promise<void> {
    const { error } = await supabase
      .from('projects')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting project:', error);
      throw new Error('Failed to delete project');
    }
  }

  /**
   * Link a candidate to a project
   */
  async linkCandidateToProject(projectId: string, candidateId: string, analysisId?: string): Promise<void> {
    const { error } = await supabase
      .from('project_candidates')
      .insert({
        project_id: projectId,
        candidate_id: candidateId,
        analysis_id: analysisId || null,
        status: 'active'
      });

    if (error && error.code !== '23505') { // Ignore unique constraint violations
      console.error('Error linking candidate to project:', error);
      throw new Error('Failed to link candidate to project');
    }
  }

  /**
   * Get candidates for a specific project
   */
  async getProjectCandidates(projectId: string): Promise<any[]> {
    const { data, error } = await supabase
      .from('project_candidates')
      .select(`
        *,
        candidates (*),
        analyses (*)
      `)
      .eq('project_id', projectId);

    if (error) {
      console.error('Error fetching project candidates:', error);
      throw new Error('Failed to fetch project candidates');
    }

    return data || [];
  }

  /**
   * Get analyses for a specific project
   */
  async getProjectAnalyses(projectId: string): Promise<any[]> {
    const { data, error } = await supabase
      .from('project_candidates')
      .select(`
        analysis_id,
        analyses (*)
      `)
      .eq('project_id', projectId)
      .not('analysis_id', 'is', null);

    if (error) {
      console.error('Error fetching project analyses:', error);
      throw new Error('Failed to fetch project analyses');
    }

    return data?.map(item => item.analyses).filter(Boolean) || [];
  }

  /**
   * Update project progress metrics
   */
  async updateProjectProgress(projectId: string, progress: {
    applicants?: number;
    interviews?: number;
    offers?: number;
    hires?: number;
  }): Promise<void> {
    const updateData: any = {
      progress: {
        applicants: progress.applicants || 0,
        interviews: progress.interviews || 0,
        offers: progress.offers || 0,
        hires: progress.hires || 0,
      }
    };

    const { error } = await supabase
      .from('projects')
      .update(updateData)
      .eq('id', projectId);

    if (error) {
      console.error('Error updating project progress:', error);
      throw new Error('Failed to update project progress');
    }
  }

  /**
   * Map database record to frontend Project interface
   */
  private mapDatabaseToProject(dbProject: any): Project {
    // Parse salary range (e.g., "50000-80000 USD" -> { min: 50000, max: 80000, currency: "USD" })
    const parseBudget = (salaryRange: string | null) => {
      if (!salaryRange) return { min: 0, max: 0, currency: 'USD' };
      
      // Enhanced regex to handle currency symbols like €, $, £, etc.
      const match = salaryRange.match(/(\d+(?:,\d+)*)\s*-\s*(\d+(?:,\d+)*)\s*([€$£¥₹\w]+)/);
      if (match) {
        return {
          min: parseInt(match[1].replace(/,/g, '')),
          max: parseInt(match[2].replace(/,/g, '')),
          currency: match[3] || 'USD'
        };
      }
      
      // Try alternative format without currency
      const altMatch = salaryRange.match(/(\d+(?:,\d+)*)\s*-\s*(\d+(?:,\d+)*)/);
      if (altMatch) {
        return {
          min: parseInt(altMatch[1].replace(/,/g, '')),
          max: parseInt(altMatch[2].replace(/,/g, '')),
          currency: 'USD'
        };
      }
      
      return { min: 0, max: 0, currency: 'USD' };
    };

    // Extract role from description (e.g., "Senior Developer position in Engineering" -> "Senior Developer")
    const parseRole = (description: string | null) => {
      if (!description) return '';
      const match = description.match(/^(.+?)\s+position\s+in/i);
      return match ? match[1] : description;
    };

    return {
      id: dbProject.id,
      title: dbProject.title,
      company: dbProject.company,
      role: parseRole(dbProject.description),
      department: dbProject.department,
      location: dbProject.location,
      status: dbProject.status,
      priority: dbProject.priority,
      createdAt: new Date(dbProject.created_at),
      deadline: new Date(dbProject.deadline),
      budget: parseBudget(dbProject.salary_range),
      team: [], // Not stored in existing schema
      progress: {
        applicants: dbProject.progress?.applicants || 0,
        interviews: dbProject.progress?.interviews || 0,
        offers: dbProject.progress?.offers || 0,
        hires: dbProject.progress?.hires || 0,
      },
      analytics: {
        totalViews: 0, // Not stored in existing schema
        applicationRate: 0, // Not stored in existing schema
        qualityScore: 0, // Not stored in existing schema
      },
      requirements: {
        experience: '', // Not stored in existing schema
        skills: dbProject.requirements || [],
        education: '', // Not stored in existing schema
      },
    };
  }
}

export const projectsService = new ProjectsService();
export default projectsService; 